/**
 * HUD layout shell: fixed screen regions that the HUD builders fill. Each region is a flex box
 * anchored to a screen edge and scaled as a whole by the UI scale (uiScale.ts), drawn at the
 * 1080p design size. Regions never reach into the centre combat zone (--hk-safe-w/h).
 *
 *   topLeft      currencies row                 topRight   round menu icons (+ settings gear)
 *   target       target frame (top centre)      right      keystone tracker + meters
 *   party        compact party frames (left)    leftCenter loot / pickup feed (newest at bottom)
 *   bottomCenter skill cluster + player cast    bottomRight keybind hints
 *
 * Regions ignore the pointer; interactive children opt in (.hk-hit, buttons, kit primitives).
 * The scale is applied on resize / setting change only (never per frame).
 */
import { el } from './dom';
import { type UiScaleSetting, resolveUiScale, uiScaleSettings } from './uiScale';

export const HUD_REGIONS = ['bottomCenter', 'topLeft', 'topRight', 'right', 'leftCenter', 'bottomRight', 'party', 'target'] as const;
export type HudRegionName = (typeof HUD_REGIONS)[number];
export type HudRegions = Readonly<Record<HudRegionName, HTMLElement>>;

export class HudLayout {
  readonly root: HTMLElement;
  readonly regions: HudRegions;
  /** Resolved UI scale currently applied. */
  scale = 1;
  private setting: UiScaleSetting = 'auto';
  private readonly unsubscribe: () => void;
  private readonly onResize = (): void => this.applyScale();

  constructor(parent: HTMLElement) {
    this.root = el('div', 'hk-layout', parent);
    const regions = {} as Record<HudRegionName, HTMLElement>;
    for (const name of HUD_REGIONS) {
      const r = el('div', `hk-region hk-r-${name}`, this.root);
      r.dataset['region'] = name;
      regions[name] = r;
    }
    this.regions = regions;
    this.unsubscribe = uiScaleSettings.subscribe((s) => {
      this.setting = s;
      this.applyScale();
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize);
      if (/[?&]debug=[^&]*\bhud\b/.test(window.location?.search ?? '')) this.showGuides(true);
    }
  }

  /** The mount point for a region. */
  region(name: HudRegionName): HTMLElement {
    return this.regions[name];
  }

  /** Outline every region and the centre keep-out zone (also `?debug=hud`). */
  showGuides(on: boolean): void {
    this.root.classList.toggle('guides', on);
  }

  private applyScale(): void {
    const vh = typeof window === 'undefined' ? 1080 : window.innerHeight;
    const k = resolveUiScale(this.setting, vh);
    this.scale = k;
    // On the document root so elements outside the HUD root (settings gear) can follow it too.
    if (typeof document !== 'undefined') document.documentElement.style.setProperty('--hk-scale', String(k));
  }

  dispose(): void {
    this.unsubscribe();
    if (typeof window !== 'undefined') window.removeEventListener('resize', this.onResize);
    this.root.remove();
  }
}
